import {
  computed,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue';

export interface NxListboxNavOptions<T> {
  /** Wrap from the last item to the first (and back). Default `false` — the ends stop. */
  loop?: boolean;
  /** How far PageUp / PageDown jump. Default `10`. */
  pageSize?: number;
  /** Items that the caret skips (they stay visible). */
  isDisabled?: (item: T, index: number) => boolean;
  /** Enter on the active item — or on the first item when nothing is active yet. */
  onSelect?: (item: T, index: number) => void;
  /** The caret moved to a new item (scroll it into view, let selection follow, …). */
  onMove?: (item: T, index: number) => void;
  /**
   * Reset the caret when the item list changes (a new search). Default `true`:
   * after typing, Enter should take the best match, not a stale position.
   */
  resetOnChange?: boolean;
  /** Prefix for the option ids. Default a unique `nx-listbox-<n>`. */
  idPrefix?: string;
}

export interface NxListboxNavKeyOptions {
  /**
   * Whether Home / End move the caret. Default `true`; pass `false` from a
   * text field, where Home / End belong to the text cursor.
   */
  homeEnd?: boolean;
}

export interface NxListboxNav<T> {
  /** The caret's index, `-1` when nothing is active. */
  activeIndex: Ref<number>;
  /** The item under the caret, or `null`. */
  activeItem: ComputedRef<T | null>;
  /** The id to put into `aria-activedescendant` — `undefined` when nothing is active. */
  activeId: ComputedRef<string | undefined>;
  /** The id of the listbox element itself (for `aria-controls`). */
  listId: string;
  /** The DOM id of the option at `index`. */
  optionId: (index: number) => string;
  /** Moves the caret by `delta` items (skipping disabled ones). */
  move: (delta: number) => void;
  /** Puts the caret on `index` (clamped; a disabled item is skipped forward). */
  moveTo: (index: number) => void;
  /** Clears the caret. */
  reset: () => void;
  /**
   * The key handler for the focused element (the listbox, or the search field
   * of a combobox). Returns `true` when it handled — and prevented — the key.
   */
  onKeydown: (event: KeyboardEvent, keyOptions?: NxListboxNavKeyOptions) => boolean;
}

let seq = 0;

/**
 * Keyboard caret for a listbox that keeps DOM focus elsewhere (the search
 * field, or the list itself) and points at the active option via
 * `aria-activedescendant`: ↑/↓, PageUp/PageDown, Home/End and Enter.
 *
 * The caret is an index into `items`; the first ↓ lands on the first item,
 * the first ↑ on the last.
 */
export function useListboxNav<T>(
  items: MaybeRefOrGetter<readonly T[]>,
  options: NxListboxNavOptions<T> = {}
): NxListboxNav<T> {
  const prefix = options.idPrefix ?? `nx-listbox-${++seq}`;
  const listId = `${prefix}-list`;
  const activeIndex = ref(-1);
  const list = computed(() => toValue(items));

  const activeItem = computed<T | null>(() => {
    const index = activeIndex.value;
    return index >= 0 && index < list.value.length ? (list.value[index] as T) : null;
  });
  const optionId = (index: number) => `${prefix}-option-${index}`;
  const activeId = computed(() =>
    activeItem.value === null ? undefined : optionId(activeIndex.value)
  );

  if (options.resetOnChange !== false) {
    watch(list, () => {
      activeIndex.value = -1;
    });
  }

  const enabled = (index: number): boolean => {
    const item = list.value[index];
    return item !== undefined && !(options.isDisabled?.(item, index) ?? false);
  };

  /** From `start`, walk in `step` direction to the first enabled index, or -1. */
  function seek(start: number, step: 1 | -1): number {
    const count = list.value.length;
    for (let i = start; i >= 0 && i < count; i += step) {
      if (enabled(i)) return i;
    }
    return -1;
  }

  function land(index: number): void {
    if (index < 0 || index === activeIndex.value) return;
    activeIndex.value = index;
    options.onMove?.(list.value[index] as T, index);
  }

  function moveTo(index: number): void {
    const count = list.value.length;
    if (count === 0) return;
    const clamped = Math.min(count - 1, Math.max(0, index));
    land(seek(clamped, 1) >= 0 ? seek(clamped, 1) : seek(clamped, -1));
  }

  function move(delta: number): void {
    const count = list.value.length;
    if (count === 0 || delta === 0) return;
    const step: 1 | -1 = delta > 0 ? 1 : -1;
    const current = activeIndex.value;
    if (current < 0) {
      land(step === 1 ? seek(0, 1) : seek(count - 1, -1));
      return;
    }
    let target = current + delta;
    if (options.loop && Math.abs(delta) === 1) {
      target = (target + count) % count;
      // Wrap search: first enabled item from the wrapped position onwards.
      for (let n = 0; n < count; n++) {
        const i = (target + step * n + count) % count;
        if (enabled(i)) return land(i);
      }
      return;
    }
    target = Math.min(count - 1, Math.max(0, target));
    const next = seek(target, step);
    // Past the last enabled item in that direction: stay put.
    land(next >= 0 ? next : seek(target, step === 1 ? -1 : 1));
  }

  function reset(): void {
    activeIndex.value = -1;
  }

  function onKeydown(event: KeyboardEvent, keyOptions: NxListboxNavKeyOptions = {}): boolean {
    if (event.isComposing || event.altKey) return false;
    const page = Math.max(1, options.pageSize ?? 10);
    const homeEnd = keyOptions.homeEnd !== false || event.ctrlKey || event.metaKey;
    switch (event.key) {
      case 'ArrowDown':
        move(1);
        break;
      case 'ArrowUp':
        move(-1);
        break;
      case 'PageDown':
        move(page);
        break;
      case 'PageUp':
        move(-page);
        break;
      case 'Home':
        if (!homeEnd) return false;
        land(seek(0, 1));
        break;
      case 'End':
        if (!homeEnd) return false;
        land(seek(list.value.length - 1, -1));
        break;
      case 'Enter': {
        const index = activeItem.value !== null ? activeIndex.value : seek(0, 1);
        if (index < 0 || !options.onSelect) return false;
        options.onSelect(list.value[index] as T, index);
        break;
      }
      default:
        return false;
    }
    event.preventDefault();
    return true;
  }

  return { activeIndex, activeItem, activeId, listId, optionId, move, moveTo, reset, onKeydown };
}
